/**
 * Will-Change Lifecycle Manager Composable
 * 
 * Manages the lifecycle of CSS will-change property for optimal GPU memory usage.
 * Sets will-change before animations and removes it after completion to free resources.
 * 
 * Features:
 * - Set will-change property before animations start
 * - Remove will-change property after animations complete (500ms delay)
 * - Track active will-change elements to prevent memory leaks
 * - Handle errors gracefully (fallback to standard rendering)
 * - Automatic cleanup on component unmount
 * 
 * Requirements: 1.4, 1.5, 2.3, 2.4, 6.5, 8.3, 8.4, 15.2, 15.3, 19.2, 21.4
 */

import { onUnmounted, ref } from 'vue';

export interface WillChangeState {
  element: HTMLElement;
  properties: string[];
  startTime: number;
  duration: number;
  timeoutId?: number;
}

export interface WillChangeOptions {
  /** Delay before removing will-change after animation (ms) */
  cleanupDelay?: number;
  /** Enable logging for debugging */
  enableLogging?: boolean;
  /** Enable performance monitoring */
  enableMonitoring?: boolean;
}

export interface WillChangeManager {
  /** Set will-change property on element before animation */
  set: (element: HTMLElement, properties: string | string[], duration?: number) => void;
  
  /** Remove will-change property from element */
  remove: (element: HTMLElement) => void;
  
  /** Get active will-change elements */
  getActiveElements: () => HTMLElement[];
  
  /** Clear all will-change properties */
  clearAll: () => void;
  
  /** Check if element has active will-change */
  isActive: (element: HTMLElement) => boolean;
}

/**
 * Create Will-Change Lifecycle Manager
 * 
 * @param options - Will-change options
 * @returns Will-Change Manager instance
 */
export function useWillChange(options: WillChangeOptions = {}): WillChangeManager {
  const {
    cleanupDelay = 500,
    enableLogging = false,
    enableMonitoring = false
  } = options;

  // Track active will-change states
  const activeStates = ref<Map<HTMLElement, WillChangeState>>(new Map());

  /**
   * Log message if logging enabled
   */
  function log(message: string, ...args: any[]) {
    if (enableLogging && !process.env.SERVER) {
      console.log(`[WillChange] ${message}`, ...args);
    }
  }

  /**
   * Log degradation event if monitoring enabled
   */
  function logDegradation(type: string, error: any) {
    if (enableMonitoring && !process.env.SERVER) {
      console.warn(`[WillChange Degradation] ${type}`, error);
    }
  }

  /**
   * Set will-change property on element before animation
   * 
   * @param element - Target element
   * @param properties - CSS properties to hint (string or array)
   * @param duration - Animation duration in ms (for automatic cleanup)
   */
  function set(
    element: HTMLElement,
    properties: string | string[],
    duration?: number
  ): void {
    try {
      // Normalize properties to array
      const propsArray = Array.isArray(properties) ? properties : [properties];
      
      // Clear any existing timeout for this element
      const existingState = activeStates.value.get(element);
      if (existingState?.timeoutId) {
        clearTimeout(existingState.timeoutId);
      }

      // Set will-change property
      const willChangeValue = propsArray.join(', ');
      element.style.willChange = willChangeValue;

      log('Set will-change', { element, properties: propsArray, duration });

      // Track state
      const state: WillChangeState = {
        element,
        properties: propsArray,
        startTime: Date.now(),
        duration: duration || 0
      };

      // Schedule automatic cleanup if duration provided
      if (duration !== undefined && duration >= 0) {
        const totalDelay = duration + cleanupDelay;
        state.timeoutId = window.setTimeout(() => {
          remove(element);
        }, totalDelay);
      }

      activeStates.value.set(element, state);

    } catch (error) {
      // Graceful degradation - continue without will-change
      logDegradation('will-change-set-failed', error);
      log('Failed to set will-change, continuing with standard rendering', error);
      
      // Ensure will-change is removed if it caused issues
      try {
        element.style.willChange = 'auto';
      } catch (cleanupError) {
        // Ignore cleanup errors
      }
    }
  }

  /**
   * Remove will-change property from element
   * 
   * @param element - Target element
   */
  function remove(element: HTMLElement): void {
    try {
      const state = activeStates.value.get(element);
      
      if (state) {
        // Clear timeout if exists
        if (state.timeoutId) {
          clearTimeout(state.timeoutId);
        }

        // Remove will-change
        element.style.willChange = 'auto';

        log('Removed will-change', { 
          element, 
          duration: Date.now() - state.startTime 
        });

        // Remove from tracking
        activeStates.value.delete(element);
      }

    } catch (error) {
      logDegradation('will-change-remove-failed', error);
      log('Failed to remove will-change', error);
      
      // Still remove from tracking to prevent memory leak
      activeStates.value.delete(element);
    }
  }

  /**
   * Get list of active will-change elements
   */
  function getActiveElements(): HTMLElement[] {
    return Array.from(activeStates.value.keys());
  }

  /**
   * Clear all will-change properties
   */
  function clearAll(): void {
    log('Clearing all will-change properties', { 
      count: activeStates.value.size 
    });

    // Remove will-change from all elements
    activeStates.value.forEach((state, element) => {
      try {
        if (state.timeoutId) {
          clearTimeout(state.timeoutId);
        }
        element.style.willChange = 'auto';
      } catch (error) {
        logDegradation('will-change-clear-failed', error);
      }
    });

    // Clear tracking
    activeStates.value.clear();
  }

  /**
   * Check if element has active will-change
   */
  function isActive(element: HTMLElement): boolean {
    return activeStates.value.has(element);
  }

  // Cleanup on unmount (only if in component context)
  try {
    onUnmounted(() => {
      log('Component unmounting, cleaning up will-change');
      clearAll();
    });
  } catch (error) {
    // Not in component context - cleanup will be manual
  }

  return {
    set,
    remove,
    getActiveElements,
    clearAll,
    isActive
  };
}
